import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Check, X, Plus, FileText, Search, ArrowUpDown, ArrowUp, ArrowDown, Eye, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getPhotoUrl } from '@/utils/photoUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Pendiente: 'default',
  Aceptada: 'outline',
  Rechazada: 'destructive',
};

const Ofertas = () => {
  const [ofertas, setOfertas] = useState([]);
  const [vehiculos, setVehiculos] = useState({});
  const [revendedores, setRevendedores] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewOfferDialog, setShowNewOfferDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedVehiculo, setExpandedVehiculo] = useState(null);
  const [newOffer, setNewOffer] = useState({
    vehiculoId: '',
    revendedorId: '',
    monto: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ofertasRes, vehiculosRes, revendedoresRes] = await Promise.all([
        axios.get(`${API}/ofertas`),
        axios.get(`${API}/vehiculos`),
        axios.get(`${API}/revendedores?includeDeleted=true`),
      ]);

      setOfertas(ofertasRes.data);

      const vehMap = {};
      vehiculosRes.data.forEach((v) => {
        vehMap[v.id] = v;
      });
      setVehiculos(vehMap);

      const revMap = {};
      revendedoresRes.data.forEach((r) => {
        revMap[r.id] = r;
      });
      setRevendedores(revMap);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar ofertas');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (ofertaId) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Aceptada' });
      toast.success('Oferta aceptada');
      loadData();
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Error al aceptar oferta');
    }
  };

  const handleReject = async (ofertaId) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      loadData();
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    }
  };

  const handleCreateOffer = async () => {
    try {
      if (!newOffer.vehiculoId || !newOffer.revendedorId || !newOffer.monto) {
        toast.error('Completa todos los campos');
        return;
      }

      await axios.post(`${API}/ofertas`, {
        ...newOffer,
        monto: parseFloat(newOffer.monto),
      });
      toast.success('Oferta creada');
      setShowNewOfferDialog(false);
      setNewOffer({ vehiculoId: '', revendedorId: '', monto: '' });
      loadData();
    } catch (error) {
      console.error('Error creating offer:', error);
      toast.error('Error al crear oferta');
    }
  };

  const filterByStatus = (status) => {
    let filtered = ofertas.filter((o) => o.estado === status);

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((o) => {
        const vehiculo = vehiculos[o.vehiculoId];
        const revendedor = revendedores[o.revendedorId];
        return (
          (vehiculo && `${vehiculo.marca} ${vehiculo.modelo}`.toLowerCase().includes(term)) ||
          (revendedor && revendedor.nombre.toLowerCase().includes(term)) ||
          (revendedor && revendedor.empresa && revendedor.empresa.toLowerCase().includes(term))
        );
      });
    }

    return filtered;
  };

  // Agrupar ofertas por vehículo
  const groupOfertasByVehiculo = (ofertasList) => {
    const grouped = {};
    ofertasList.forEach((oferta) => {
      if (!grouped[oferta.vehiculoId]) {
        grouped[oferta.vehiculoId] = [];
      }
      grouped[oferta.vehiculoId].push(oferta);
    });
    return grouped;
  };

  const toggleVehiculo = (vehiculoId) => {
    if (expandedVehiculo === vehiculoId) {
      setExpandedVehiculo(null);
    } else {
      setExpandedVehiculo(vehiculoId);
    }
  };

  const [sortField, setSortField] = useState('hora');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortOfertas = (ofertasToSort) => {
    return [...ofertasToSort].sort((a, b) => {
      let aVal, bVal;

      if (sortField === 'hora') {
        aVal = new Date(a.hora);
        bVal = new Date(b.hora);
      } else if (sortField === 'monto') {
        aVal = a.monto;
        bVal = b.monto;
      } else if (sortField === 'vehiculo') {
        const vehA = vehiculos[a.vehiculoId];
        const vehB = vehiculos[b.vehiculoId];
        aVal = vehA ? `${vehA.marca} ${vehA.modelo}` : '';
        bVal = vehB ? `${vehB.marca} ${vehB.modelo}` : '';
      } else if (sortField === 'revendedor') {
        const revA = revendedores[a.revendedorId];
        const revB = revendedores[b.revendedorId];
        aVal = revA?.nombre || '';
        bVal = revB?.nombre || '';
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ field, children }) => {
    const isActive = sortField === field;
    return (
      <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort(field)}>
        <div className="flex items-center gap-2">
          {children}
          {isActive ? (
            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4 opacity-30" />
          )}
        </div>
      </th>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  const nuevasOfertas = filterByStatus('Pendiente');
  const aceptadas = filterByStatus('Aceptada');
  const rechazadas = filterByStatus('Rechazada');

  return (
    <div className="space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Ofertas</h1>
              <p className="text-muted-foreground mt-1">
                {ofertas.length} ofertas en total
              </p>
            </div>
            <Button onClick={() => setShowNewOfferDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Cargar Oferta Manual
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Búsqueda */}
        <Card className="p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por vehículo, revendedor o empresa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-offer-input"
            />
          </div>
        </Card>

        <Tabs defaultValue="nuevas" className="space-y-4">
          <TabsList>
            <TabsTrigger value="nuevas" data-testid="offers-tab-new">
              Nuevas ({nuevasOfertas.length})
            </TabsTrigger>
            <TabsTrigger value="aceptadas">Aceptadas ({aceptadas.length})</TabsTrigger>
            <TabsTrigger value="rechazadas">Rechazadas ({rechazadas.length})</TabsTrigger>
          </TabsList>

          {[
            { value: 'nuevas', ofertas: nuevasOfertas, label: 'nuevas' },
            { value: 'aceptadas', ofertas: aceptadas, label: 'aceptadas' },
            { value: 'rechazadas', ofertas: rechazadas, label: 'rechazadas' },
          ].map(({ value, ofertas: tabOfertas, label }) => {
            const groupedOfertas = groupOfertasByVehiculo(tabOfertas);
            const vehiculoIds = Object.keys(groupedOfertas);

            return (
              <TabsContent key={value} value={value} className="space-y-4">
                {tabOfertas.length === 0 ? (
                  <Card className="p-12 text-center">
                    <p className="text-muted-foreground">No hay ofertas {label}</p>
                  </Card>
                ) : (
                  <Card>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b">
                          <tr>
                            <th className="text-left p-4 font-medium w-12"></th>
                            <th className="text-left p-4 font-medium w-20">Foto</th>
                            <th className="text-left p-4 font-medium">Vehículo</th>
                            <th className="text-left p-4 font-medium">Precio Base</th>
                            <th className="text-left p-4 font-medium">Ofertas</th>
                            <th className="text-right p-4 font-medium">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehiculoIds.map((vehiculoId) => {
                            const vehiculo = vehiculos[vehiculoId];
                            const vehiculoOfertas = groupedOfertas[vehiculoId];
                            const isExpanded = expandedVehiculo === vehiculoId;
                            const ofertasCount = vehiculoOfertas.length;

                            return (
                              <React.Fragment key={vehiculoId}>
                                {/* Fila del vehículo */}
                                <tr
                                  className="border-b hover:bg-accent/50 cursor-pointer transition-colors duration-200"
                                  onClick={() => toggleVehiculo(vehiculoId)}
                                  data-testid="vehiculo-row"
                                >
                                  <td className="p-4">
                                    <div className="transition-transform duration-300">
                                      {isExpanded ? (
                                        <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-300" />
                                      ) : (
                                        <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform duration-300" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-4">
                                    <div className="w-16 h-16 rounded-md overflow-hidden bg-muted">
                                      {vehiculo?.fotos && vehiculo.fotos[0] ? (
                                        <img src={getPhotoUrl(vehiculo.fotos[0])} alt={vehiculo.marca} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <FileText className="h-6 w-6 text-muted-foreground" />
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-4">
                                    <div className="font-medium">
                                      {vehiculo ? `${vehiculo.marca} ${vehiculo.modelo}` : 'N/A'}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      {vehiculo?.version} • {vehiculo?.anio}
                                    </div>
                                  </td>
                                  <td className="p-4">
                                    <div className="font-medium">
                                      ${vehiculo?.precioBase?.toLocaleString('en-US') || 'N/A'}
                                    </div>
                                  </td>
                                  <td className="p-4">
                                    <Badge variant="secondary">
                                      {ofertasCount} {ofertasCount === 1 ? 'oferta' : 'ofertas'}
                                    </Badge>
                                  </td>
                                  <td className="p-4">
                                    <div className="flex justify-end gap-2">
                                      {vehiculo && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          asChild
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Link to={`/admin/publicaciones/${vehiculo.id}`}>
                                            <Eye className="h-4 w-4" />
                                          </Link>
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>

                                {/* Filas de ofertas expandidas */}
                                {isExpanded && vehiculoOfertas.map((oferta, index) => {
                                  const revendedor = revendedores[oferta.revendedorId];
                                  return (
                                    <tr
                                      key={oferta.id}
                                      className="border-b last:border-0 bg-accent/20 animate-in fade-in slide-in-from-top-2"
                                      style={{ animationDelay: `${index * 50}ms`, animationDuration: '300ms' }}
                                      data-testid="offers-row"
                                    >
                                      <td className="p-4"></td>
                                      <td className="p-4"></td>
                                      <td className="p-4 pl-8">
                                        <div className="font-medium">
                                          {revendedor?.nombre || 'N/A'}
                                          {revendedor?.deletedAt && (
                                            <span className="ml-2 text-xs text-muted-foreground" title={`Eliminado el ${new Date(revendedor.deletedAt).toLocaleDateString('es-UY')}`}>
                                              (eliminado)
                                            </span>
                                          )}
                                        </div>
                                        {revendedor?.empresa && (
                                          <div className="text-sm text-muted-foreground">{revendedor.empresa}</div>
                                        )}
                                      </td>
                                      <td className="p-4">
                                        <div className="text-lg font-bold text-primary">
                                          ${oferta.monto.toLocaleString('en-US')}
                                        </div>
                                      </td>
                                      <td className="p-4 text-sm text-muted-foreground">
                                        {format(new Date(oferta.hora), 'dd/MM/yyyy HH:mm')}
                                      </td>
                                      <td className="p-4">
                                        <div className="flex justify-end gap-2">
                                          {oferta.estado === 'Pendiente' && (
                                            <>
                                              <Button
                                                size="sm"
                                                variant="default"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleAccept(oferta.id);
                                                }}
                                                data-testid="offers-accept-button"
                                              >
                                                <Check className="h-4 w-4" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleReject(oferta.id);
                                                }}
                                                data-testid="offers-reject-button"
                                              >
                                                <X className="h-4 w-4" />
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {/* New Offer Dialog */}
      <Dialog open={showNewOfferDialog} onOpenChange={setShowNewOfferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cargar Oferta Manual</DialogTitle>
            <DialogDescription>
              Registra una oferta recibida de forma manual
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="vehiculo">Vehículo</Label>
              <Select
                value={newOffer.vehiculoId}
                onValueChange={(value) => setNewOffer({ ...newOffer, vehiculoId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar vehículo..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(vehiculos).map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.marca} {v.modelo} {v.anio}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="revendedor">Revendedor</Label>
              <Select
                value={newOffer.revendedorId}
                onValueChange={(value) => setNewOffer({ ...newOffer, revendedorId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar revendedor..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(revendedores).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.nombre} - {r.empresa}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="monto">Monto (USD)</Label>
              <Input
                id="monto"
                type="number"
                placeholder="15000"
                value={newOffer.monto}
                onChange={(e) => setNewOffer({ ...newOffer, monto: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewOfferDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateOffer}>Crear Oferta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Ofertas;